import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Sparkles, Lock, Check, FileDown, Loader2 } from "lucide-react";
import { fetchPackages, createCheckout } from "../lib/pro";
import { toast } from "sonner";

const PACKAGE_COPY = {
  pro_30d: {
    icon: Sparkles,
    headline: "Unlock everything.",
    perks: [
      "AI advisor memo (Owner + Analyst lenses)",
      "Unlimited PDF exports",
      "Industry-specific recommendations",
      "30 days, on this device",
    ],
    cta: "Start Pro Pass",
    badge: "Most popular",
  },
  pdf_single: {
    icon: FileDown,
    headline: "One PDF, forever.",
    perks: [
      "PDF for this report",
      "Print or share with your bank",
      "Tied to one report — never expires",
    ],
    cta: "Buy single PDF",
  },
};

export default function ProUnlockDialog({
  trigger,
  reportId = null,
  defaultPackage = "pro_30d",
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}) {
  const [open, setOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : open;
  const setOpen2 = isControlled ? setControlledOpen : setOpen;

  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyPkg, setBusyPkg] = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    fetchPackages()
      .then((p) => setPackages(p))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [isOpen]);

  async function handleBuy(pkg) {
    setBusyPkg(pkg.id);
    try {
      const session = await createCheckout({
        packageId: pkg.id,
        reportId: pkg.id === "pdf_single" ? reportId : null,
      });
      if (!session?.url) throw new Error("No checkout URL returned.");
      window.location.href = session.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Try again.");
      setBusyPkg(null);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setOpen2}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className="bg-[#F7F5F0] border-[#E5E0D8] max-w-2xl"
        data-testid="pro-unlock-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#1F382E] tracking-tight">
            Unlock the full CFO Grade.
          </DialogTitle>
          <DialogDescription className="text-[#5C665D] leading-relaxed">
            The dashboard, ratios and benchmarks above are free, forever. Unlock the AI advisor
            memo and PDF export when you're ready to take it to a banker, board or buyer.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-10 flex items-center justify-center text-[#5C665D]">
            <Loader2 className="animate-spin mr-2" size={16} /> Loading packages…
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4 mt-2">
            {packages.map((pkg) => {
              const copy = PACKAGE_COPY[pkg.id] || {};
              const Icon = copy.icon || Sparkles;
              const isFeatured = pkg.id === defaultPackage;
              const busy = busyPkg === pkg.id;
              const unavailable = pkg.available === false;
              return (
                <div
                  key={pkg.id}
                  className={`relative bg-white border rounded-xl p-6 flex flex-col transition-all duration-300 ${
                    unavailable
                      ? "border-[#E5E0D8] opacity-60"
                      : isFeatured
                        ? "border-[#1F382E] shadow-sm"
                        : "border-[#E5E0D8] hover:border-[#C26D5C]/40"
                  }`}
                  data-testid={`pro-package-${pkg.id}`}
                >
                  {copy.badge && isFeatured && !unavailable && (
                    <span className="absolute -top-2.5 left-5 bg-[#C26D5C] text-white text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full">
                      {copy.badge}
                    </span>
                  )}
                  {unavailable && (
                    <span className="absolute -top-2.5 left-5 bg-[#9c6b1d] text-white text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full">
                      Temporarily unavailable
                    </span>
                  )}
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={16} strokeWidth={1.5} className="text-[#C26D5C]" />
                    <span className="text-xs uppercase tracking-[0.18em] text-[#5C665D]">
                      {pkg.label}
                    </span>
                  </div>
                  <p className="font-serif-display text-2xl text-[#1F382E] tracking-tight mt-1">
                    {copy.headline}
                  </p>
                  <p className="font-serif-display text-4xl text-[#1F382E] tabular-nums mt-3">
                    ${pkg.amount.toFixed(0)}
                    <span className="text-sm text-[#5C665D] font-sans font-normal">
                      {pkg.duration_days ? ` / 30 days` : ` one-time`}
                    </span>
                  </p>
                  <ul className="mt-4 space-y-2 flex-1">
                    {(copy.perks || [pkg.description]).map((p, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-[#1A1A1A] leading-relaxed"
                      >
                        <Check
                          size={14}
                          strokeWidth={2}
                          className="mt-0.5 text-[#2C5C43] shrink-0"
                        />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                  {unavailable ? (
                    <div className="mt-6 w-full bg-[#FAF5EC] border border-[#9c6b1d]/30 text-[#9c6b1d] rounded-md py-2.5 px-3 text-xs leading-relaxed text-center">
                      {pkg.blocked_reason || "Currently unavailable. Try again soon."}
                    </div>
                  ) : (
                    <>
                      {pkg.degraded_note && (
                        <div
                          className="mt-5 w-full bg-[#FAF5EC] border border-[#9c6b1d]/25 text-[#9c6b1d] rounded-md py-2 px-3 text-[11px] leading-relaxed"
                          data-testid={`pro-degraded-${pkg.id}`}
                        >
                          {pkg.degraded_note}
                        </div>
                      )}
                      <Button
                        onClick={() => handleBuy(pkg)}
                        disabled={busy}
                        className={`mt-6 w-full transition-all duration-300 ${
                          isFeatured
                            ? "bg-[#1F382E] hover:bg-[#142922] text-[#F7F5F0]"
                            : "bg-white border border-[#1F382E] text-[#1F382E] hover:bg-[#1F382E] hover:text-[#F7F5F0]"
                        }`}
                        data-testid={`pro-buy-${pkg.id}-btn`}
                      >
                        {busy ? (
                          <>
                            <Loader2 className="animate-spin mr-2" size={14} /> Redirecting…
                          </>
                        ) : (
                          copy.cta || `Buy ${pkg.label}`
                        )}
                      </Button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-start gap-2 text-[11px] text-[#5C665D] leading-relaxed mt-2">
          <Lock size={11} strokeWidth={1.5} className="mt-0.5 shrink-0" />
          <span>
            Secure checkout via Stripe. No account required. Your unlock is bound to this device.
          </span>
        </div>

        <a
          href="/sample-report"
          target="_blank"
          rel="noopener noreferrer"
          className="block mt-1 text-center text-[12px] text-[#C26D5C] hover:text-[#1F382E] underline-offset-2 hover:underline transition-colors"
          data-testid="pro-unlock-sample-link"
        >
          See a sample report (no signup) →
        </a>
      </DialogContent>
    </Dialog>
  );
}
